<template>
  <div id="bulkMailModal">
    <div
      class="modal fade"
      id="bulkMailModalDialog"
      tabindex="-1"
      role="dialog"
      aria-labelledby="bulkMailModalLabel"
      aria-hidden="true"
      data-backdrop="static"
      data-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header bg-spnm text-white">
            <h6 class="modal-title" id="bulkMailModalLabel">
              <i class="fas fa-envelope-bulk mr-2"></i> Bulk Mail
            </h6>
            <button
              type="button"
              class="close text-white"
              @click="closeModal"
              :disabled="currentStep === 2"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <!-- Step Indicator -->
          <div class="px-4 pt-3 pb-0">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="step-indicator" :class="{ active: currentStep >= 1, done: currentStep > 1 }">
                <span>1</span>
              </div>
              <div class="step-line" :class="{ done: currentStep > 1 }"></div>
              <div class="step-indicator" :class="{ active: currentStep >= 2, done: currentStep > 2 }">
                <span>2</span>
              </div>
              <div class="step-line" :class="{ done: currentStep > 2 }"></div>
              <div class="step-indicator" :class="{ active: currentStep >= 3 }">
                <span>3</span>
              </div>
            </div>
            <div class="d-flex justify-content-between px-2 mb-0" style="font-size: 0.7rem; color: #666;">
              <span style="margin-left:-6px">Configure</span>
              <span style="margin-left:22px">Sending</span>
              <span style="margin-right:-6px">Results</span>
            </div>
          </div>

          <hr class="my-2" />

          <!-- STEP 1: Configure -->
          <div v-if="currentStep === 1">
            <form @submit.prevent="startSending">
              <div class="modal-body">

                <!-- Source Selection -->
                <div class="form-group">
                  <label class="font-weight-bold">
                    <i class="fas fa-users mr-1 text-primary"></i> Recipients
                  </label>
                  <div class="row">
                    <div class="col-6 col-md-3 mb-2" v-for="opt in sourceOptions" :key="opt.value">
                      <div
                        class="source-card p-2 border rounded text-center"
                        :class="{ 'border-primary bg-light-blue': sourceType === opt.value }"
                        style="cursor:pointer;"
                        @click="sourceType = opt.value"
                      >
                        <i :class="opt.icon + ' fa-lg mb-1 text-primary'"></i>
                        <div style="font-size:0.75rem; font-weight:600;">{{ opt.label }}</div>
                        <span class="badge badge-pill badge-primary mt-1">
                          {{ recipientCount(opt.value) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <small class="text-muted">
                    <i class="fas fa-info-circle mr-1"></i>
                    {{ filteredRecipients.length }} recipient{{ filteredRecipients.length !== 1 ? 's' : '' }} selected
                    <span v-if="filteredRecipients.length > 0">
                      &bull; {{ unsubscribedCount }} unsubscribed will be skipped
                    </span>
                  </small>
                </div>

                <!-- Template Selection -->
                <div class="form-group mt-3">
                  <label class="font-weight-bold">
                    <i class="fas fa-file-code mr-1 text-primary"></i> Email Template
                  </label>
                  <select
                    class="form-control form-control-sm"
                    v-model="selectedTemplate"
                    @change="onTemplateChange"
                    required
                  >
                    <option :value="null" disabled>— Select a template —</option>
                    <option
                      v-for="t in templateList"
                      :key="t.fileId"
                      :value="t"
                    >{{ t.fileName }}</option>
                  </select>
                  <div class="mt-1">
                    <small>
                      <a
                        data-toggle="collapse"
                        href="#addTemplateCollapse"
                        role="button"
                        aria-expanded="false"
                        aria-controls="addTemplateCollapse"
                      >
                        <i class="fas fa-plus-circle mr-1"></i><u>Add new template</u>
                      </a>
                    </small>
                  </div>

                  <!-- Add New Template (collapsible) -->
                  <div class="collapse mt-2" id="addTemplateCollapse">
                    <div class="card card-body p-2">
                      <form @submit.prevent="addEmailTemplate">
                        <div class="form-group form-group-sm mb-2">
                          <label style="font-size:0.8rem">Description (used as default subject)</label>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="e.g. SPNM Event Invitation – May 2026"
                            v-model="newTemplate.description"
                            required
                          />
                        </div>
                        <div class="custom-file mb-2">
                          <input
                            type="file"
                            class="custom-file-input"
                            id="bulkMailTemplateFile"
                            @change="onFileChange"
                            accept=".html"
                            required
                          />
                          <label class="custom-file-label" for="bulkMailTemplateFile">
                            {{ newTemplate.file ? newTemplate.file.name : 'Choose HTML file...' }}
                          </label>
                        </div>
                        <button class="btn btn-spnm btn-sm" type="submit" :disabled="addingTemplate">
                          <span v-if="addingTemplate"><i class="fas fa-spinner fa-spin mr-1"></i>Uploading...</span>
                          <span v-else><i class="fas fa-upload mr-1"></i>Upload Template</span>
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                <!-- Template Preview -->
                <div v-if="selectedTemplate" class="form-group mt-3">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <label class="font-weight-bold mb-0">
                      <i class="fas fa-eye mr-1 text-primary"></i> Preview
                    </label>
                    <div class="btn-group btn-group-sm" role="group">
                      <button type="button"
                              class="btn"
                              :class="!previewDark ? 'btn-light active' : 'btn-outline-secondary'"
                              @click="previewDark = false"
                              title="Light mode preview">
                        <i class="fas fa-sun"></i> Light
                      </button>
                      <button type="button"
                              class="btn"
                              :class="previewDark ? 'btn-dark active' : 'btn-outline-secondary'"
                              @click="previewDark = true"
                              title="Dark mode preview">
                        <i class="fas fa-moon"></i> Dark
                      </button>
                    </div>
                  </div>
                  <div class="template-preview-wrap" :class="{ 'template-preview-dark': previewDark }">
                    <div v-if="previewLoading" class="template-preview-loading">
                      <span class="spinner-border spinner-border-sm text-primary mr-1"></span> Loading preview…
                    </div>
                    <iframe v-else
                      :srcdoc="previewDark ? darkPreviewContent : templateContent"
                      class="template-preview-iframe"
                      sandbox="allow-same-origin"
                    ></iframe>
                  </div>
                </div>

                <!-- Email Subject -->
                <div class="form-group mt-3">
                  <label class="font-weight-bold">
                    <i class="fas fa-heading mr-1 text-primary"></i> Email Subject
                  </label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="emailSubject"
                    placeholder="Email subject line"
                    required
                  />
                </div>

              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary btn-sm" @click="closeModal">
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn-spnm btn-sm"
                  :disabled="!canSend"
                >
                  <i class="fas fa-paper-plane mr-1"></i>
                  Start Sending to {{ filteredRecipients.length }} recipients
                </button>
              </div>
            </form>
          </div>

          <!-- STEP 2: Sending Progress -->
          <div v-if="currentStep === 2">
            <div class="modal-body text-center py-4">
              <div class="mb-3">
                <i class="fas fa-envelope fa-2x text-primary"></i>
                <h6 class="mt-2 mb-0">Sending emails, please wait...</h6>
                <small class="text-muted">Do not close this window.</small>
              </div>

              <!-- Progress bar -->
              <div class="progress mb-2" style="height: 22px; border-radius: 11px;">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  role="progressbar"
                  :style="{ width: progressPercent + '%' }"
                  :aria-valuenow="jobStatus.processed"
                  :aria-valuemin="0"
                  :aria-valuemax="jobStatus.total"
                >
                  {{ progressPercent }}%
                </div>
              </div>

              <div class="d-flex justify-content-between px-1 mt-1" style="font-size: 0.8rem;">
                <span>
                  <i class="fas fa-circle-notch fa-spin text-primary mr-1"></i>
                  {{ jobStatus.processed }} / {{ jobStatus.total }} processed
                </span>
                <span>
                  <span class="text-success font-weight-bold">{{ jobStatus.succeeded }} sent</span>
                  &nbsp;&bull;&nbsp;
                  <span class="text-danger font-weight-bold">{{ jobStatus.failed }} failed</span>
                </span>
              </div>
            </div>
            <div class="modal-footer">
              <span class="text-muted" style="font-size:0.8rem;">
                <i class="fas fa-lock mr-1"></i> Please wait until sending completes.
              </span>
            </div>
          </div>

          <!-- STEP 3: Results -->
          <div v-if="currentStep === 3">
            <div class="modal-body">
              <!-- Summary badges -->
              <div class="d-flex justify-content-center mb-3">
                <div class="result-badge result-badge-success mr-3">
                  <i class="fas fa-check-circle fa-2x"></i>
                  <div class="mt-1 font-weight-bold" style="font-size:1.4rem;">{{ jobStatus.succeeded }}</div>
                  <div style="font-size:0.75rem;">Sent Successfully</div>
                </div>
                <div class="result-badge result-badge-danger">
                  <i class="fas fa-times-circle fa-2x"></i>
                  <div class="mt-1 font-weight-bold" style="font-size:1.4rem;">{{ jobStatus.failed }}</div>
                  <div style="font-size:0.75rem;">Failed</div>
                </div>
              </div>

              <!-- Failed list -->
              <div v-if="jobStatus.failed > 0">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="font-weight-bold text-danger">
                    <i class="fas fa-exclamation-triangle mr-1"></i>Failed Emails
                  </span>
                  <button class="btn btn-outline-secondary btn-sm" @click="downloadFailedCSV">
                    <i class="fas fa-download mr-1"></i> Download CSV
                  </button>
                </div>
                <div style="max-height: 220px; overflow-y: auto;">
                  <table class="table table-sm table-bordered table-hover">
                    <thead class="thead-light">
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(entry, idx) in jobStatus.failedEntries" :key="idx">
                        <td>{{ idx + 1 }}</td>
                        <td>{{ entry.name }}</td>
                        <td>{{ entry.email }}</td>
                        <td class="text-danger" style="font-size:0.75rem;">{{ entry.error }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else class="text-success text-center">
                <i class="fas fa-check-circle fa-lg mr-1"></i>
                All emails were sent successfully!
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-spnm btn-sm" @click="closeModal">
                <i class="fas fa-times mr-1"></i> Close
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import $ from "jquery";

export default {
  name: "BulkMailModal",
  props: {
    allMailingList: {
      type: Array,
      default: () => [],
    },
    preSelectedList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentStep: 1,
      sourceType: "selected",
      sourceOptions: [
        { value: "selected", label: "Selected", icon: "fas fa-check-square" },
        { value: "members", label: "Members Only", icon: "fas fa-id-card" },
        { value: "nonmembers", label: "Non-Members", icon: "fas fa-user" },
        { value: "all", label: "All Contacts", icon: "fas fa-users" },
      ],
      templateList: [],
      selectedTemplate: null,
      emailSubject: "",
      newTemplate: { description: "", file: null },
      addingTemplate: false,
      // preview
      templateContent: "",
      previewLoading: false,
      previewDark: false,
      jobId: null,
      pollInterval: null,
      jobStatus: {
        status: "running",
        total: 0,
        processed: 0,
        succeeded: 0,
        failed: 0,
        failedEntries: [],
      },
    };
  },
  computed: {
    filteredRecipients() {
      const list = this.allMailingList || [];
      if (this.sourceType === "selected") return this.preSelectedList || [];
      if (this.sourceType === "members")
        return list.filter((r) => r.isMember === "Y" || r.isMember === "y");
      if (this.sourceType === "nonmembers")
        return list.filter((r) => r.isMember !== "Y" && r.isMember !== "y");
      return list; // all
    },
    unsubscribedCount() {
      return this.filteredRecipients.filter(
        (r) => r.subscribed === "N" || r.subscribed === "n"
      ).length;
    },
    progressPercent() {
      if (!this.jobStatus.total) return 0;
      return Math.round((this.jobStatus.processed / this.jobStatus.total) * 100);
    },
    canSend() {
      return (
        this.filteredRecipients.length > 0 &&
        this.selectedTemplate !== null &&
        this.emailSubject.trim().length > 0
      );
    },
    darkPreviewContent() {
      const darkStyle = `<style>
        html,body{background:#1e1e1e!important;color:#d4d4d4!important;}
        *{color:inherit;border-color:#444!important;}
        a{color:#6db3f2!important;}
      </style>`;
      if (!this.templateContent) return darkStyle;
      if (this.templateContent.includes("</head>"))
        return this.templateContent.replace("</head>", darkStyle + "</head>");
      return darkStyle + this.templateContent;
    },
  },
  watch: {
    selectedTemplate(val) {
      if (!val) { this.templateContent = ""; return; }
      this.previewLoading = true;
      axios.get("/api/Event/GetTemplate", { params: { fileName: val.fileName } })
        .then((r) => { this.templateContent = r.data.content || ""; })
        .catch(() => { this.templateContent = "<p style='color:red;padding:12px'>Could not load preview.</p>"; })
        .finally(() => { this.previewLoading = false; });
    },
  },
  created() {
    this.getTemplateList();
  },
  methods: {
    recipientCount(sourceValue) {
      const list = this.allMailingList || [];
      if (sourceValue === "selected") return (this.preSelectedList || []).length;
      if (sourceValue === "members")
        return list.filter((r) => r.isMember === "Y" || r.isMember === "y").length;
      if (sourceValue === "nonmembers")
        return list.filter((r) => r.isMember !== "Y" && r.isMember !== "y").length;
      return list.length;
    },

    getTemplateList() {
      axios
        .post("/api/FileUploader/FindUploadedFiles", { FileType: "EmailTemplate" })
        .then((res) => {
          this.templateList = res.data.uploadedFileItems || [];
        })
        .catch(() => {});
    },

    onTemplateChange() {
      if (this.selectedTemplate && !this.emailSubject) {
        this.emailSubject = this.selectedTemplate.description || "";
      }
    },

    onFileChange(event) {
      this.newTemplate.file = event.target.files[0];
    },

    addEmailTemplate() {
      if (!this.newTemplate.file || !this.newTemplate.description) return;
      this.addingTemplate = true;
      const formData = new FormData();
      formData.append("File", this.newTemplate.file);
      formData.append(
        "ReferenceData",
        JSON.stringify({ description: this.newTemplate.description })
      );
      axios
        .post("/api/FileUploader/UploadFile", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(() => {
          this.newTemplate = { description: "", file: null };
          this.addingTemplate = false;
          this.getTemplateList();
          $("#addTemplateCollapse").collapse("hide");
        })
        .catch(() => {
          this.addingTemplate = false;
          alert("Failed to upload template.");
        });
    },

    startSending() {
      if (!this.canSend) return;

      const requestBody = {
        mailingListCollection: this.filteredRecipients,
        fileInformation: this.selectedTemplate,
        subject: this.emailSubject,
      };

      this.$store.dispatch("increaseLoadingCounter");

      axios
        .post("/api/BulkMail/Start", requestBody)
        .then((res) => {
          this.$store.dispatch("decreaseLoadingCounter");
          this.jobId = res.data.jobId;
          this.currentStep = 2;
          this.jobStatus = {
            status: "running",
            total: this.filteredRecipients.length,
            processed: 0,
            succeeded: 0,
            failed: 0,
            failedEntries: [],
          };
          this.startPolling();
        })
        .catch((err) => {
          this.$store.dispatch("decreaseLoadingCounter");
          const msg =
            err.response?.data || "Failed to start bulk mail. Please try again.";
          alert(msg);
        });
    },

    startPolling() {
      this.pollInterval = setInterval(() => {
        this.pollProgress();
      }, 2000);
    },

    pollProgress() {
      if (!this.jobId) return;
      axios
        .get(`/api/BulkMail/Progress/${this.jobId}`)
        .then((res) => {
          const data = res.data;
          this.jobStatus = {
            status: data.status,
            total: data.total,
            processed: data.processed,
            succeeded: data.succeeded,
            failed: data.failed,
            failedEntries: data.failedEntries || [],
          };
          if (data.status === "completed" || data.status === "failed") {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
            this.currentStep = 3;
          }
        })
        .catch(() => {
          // Network hiccup — keep polling
        });
    },

    downloadFailedCSV() {
      const headers = ["Name", "Email", "Error"];
      const rows = this.jobStatus.failedEntries.map((e) => [
        `"${(e.name || "").replace(/"/g, '""')}"`,
        `"${(e.email || "").replace(/"/g, '""')}"`,
        `"${(e.error || "").replace(/"/g, '""')}"`,
      ]);
      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BulkMail_Failed_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    closeModal() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      $("#bulkMailModalDialog").modal("hide");
      // Reset after modal closes so it's fresh on next open
      setTimeout(() => {
        this.currentStep = 1;
        this.sourceType = "selected";
        this.selectedTemplate = null;
        this.emailSubject = "";
        this.jobId = null;
        this.jobStatus = {
          status: "running",
          total: 0,
          processed: 0,
          succeeded: 0,
          failed: 0,
          failedEntries: [],
        };
      }, 400);
    },
  },
  beforeUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval);
  },
};
</script>

<style scoped>
/* Step indicator */
.step-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #dee2e6;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  transition: background 0.3s, color 0.3s;
}
.step-indicator.active {
  background: #3b5aa7;
  color: #fff;
}
.step-indicator.done {
  background: #28a745;
  color: #fff;
}
.step-line {
  flex: 1;
  height: 3px;
  background: #dee2e6;
  margin: 0 4px;
  transition: background 0.3s;
}
.step-line.done {
  background: #28a745;
}

/* Source cards */
.source-card {
  transition: border-color 0.2s, background 0.2s;
}
.source-card:hover {
  border-color: #3b5aa7 !important;
}
.bg-light-blue {
  background-color: #eef2ff !important;
}

/* Result badges */
.result-badge {
  text-align: center;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  min-width: 110px;
}
.result-badge-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}
.result-badge-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* SPNM primary header color override */
.bg-spnm {
  background-color: #3b5aa7;
}

/* Template preview */
.template-preview-wrap {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  transition: background 0.2s, border-color 0.2s;
}
.template-preview-dark {
  background: #1e1e1e;
  border-color: #444;
}
.template-preview-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6c757d;
  font-size: 0.85rem;
}
.template-preview-iframe {
  display: block;
  width: 100%;
  height: 260px;
  border: none;
}
</style>
